import axios from "axios";
import { API_URL } from "../utils/Constant";

const ESV_API_WRAPPER_PATH = "call_esv_api/";

const HTTP_METHOD = {
  GET: "GET",
  POST: "POST",
  PATCH: "PATCH",
};

const SPEC_MANAGER_NOT_ONBOARDED_ON_PRODUCT_MESSAGE = "Customer is not onboarded on Product submission";
const SPEC_MANAGER_NOT_ONBOARDED_ON_ENVIRONMENT_MESSAGE = "Customer is not onboarded on Environment submission";

const getAxiosErrorMessage = (error, fallback = "Something went wrong.") => {
  if (error.response && error.response.data && error.response.data.message) {
    return error.response.data.message;
  }
  return fallback;
};

const resolvePointLocationIdentifier = (point) => {
  if (point.location_identifier != null) {
    return point.location_identifier;
  }
  if (point.point_identifier != null) {
    return point.point_identifier;
  }
  return {};
};

const resolveSpecManagerCustomerContext = (customerContext) => {
  if (!customerContext || !customerContext.company) {
    throw new Error("customerContext.company is required for spec manager API calls");
  }

  const customerId = customerContext.customer_id != null
    ? String(customerContext.customer_id).trim()
    : "";

  return {
    companyName: customerContext.company,
    customerIdForUserPayload: customerId,
  };
};

const buildEsvWrapperRequestBody = ({
  apiName,
  callType,
  customerContext,
  payload = {},
}) => {
  const { companyName, customerIdForUserPayload } = resolveSpecManagerCustomerContext(customerContext);

  return {
    api_name: apiName,
    call_type: callType,
    customer_id: customerIdForUserPayload,
    payload: {
      company_name: companyName,
      ...payload,
    },
  };
};

const requestEsvThroughWrapper = async ({
  apiName,
  callType,
  customerContext,
  payload = {},
  signal,
}) => {
  const requestBody = buildEsvWrapperRequestBody({
    apiName,
    callType,
    customerContext,
    payload,
  });

  const response = await axios.post(`${API_URL}/${ESV_API_WRAPPER_PATH}`, requestBody, {
    signal,
  });

  return response;
};

export const getSpecsData = async (payload, isUpdating = false, signal, customerContext) => {
  try {
    const requestPayload = {
      filter: payload.filter,
      search_value: payload.search,
      product_id: payload.product_id,
      specs: payload.specs,
      page: payload.page,
      search_field: payload.search_field,
    };

    let response;
    if (isUpdating) {
      requestPayload.action = payload.action ? payload.action : "update_specs";
      response = await requestEsvThroughWrapper({
        apiName: "specsmanager/",
        callType: HTTP_METHOD.PATCH,
        customerContext,
        payload: requestPayload,
        signal,
      });
    } else {
      response = await requestEsvThroughWrapper({
        apiName: "specsmanager/",
        callType: HTTP_METHOD.GET,
        customerContext,
        payload: requestPayload,
        signal,
      });
    }

    const {
      prev_page_link: prevPageLink,
      next_page_link: nextPageLink,
      last_page_link: lastPageLink,
      total_page: totalPage,
    } = response.data;
    const pages = {
      prevPageLink, nextPageLink, lastPageLink, totalPage,
    };

    if (response.data.message === "Success" || response.data.message === "success") {
      return { success: true, files: response.data.result, pages };
    }
    if (response.data.message === "Specs and status updated, reports from last 30 days updated") {
      return { success: true };
    }

    return { success: false, message: "Something went wrong." };
  } catch (error) {
    return {
      success: false,
      message: "Something went wrong.",
      canceled: error.code === "ERR_CANCELED",
    };
  }
};

export const getGlobalSpecs = async (params = {}, signal, customerContext) => {
  try {
    const response = await requestEsvThroughWrapper({
      apiName: "globalspecs/",
      callType: HTTP_METHOD.GET,
      customerContext,
      payload: params,
      signal,
    });
    const { message } = response.data;
    const {
      prev_page_link: prevPageLink,
      next_page_link: nextPageLink,
      last_page_link: lastPageLink,
      total_page: totalPage,
    } = response.data;
    return {
      success: message === "Success",
      result: response.data.result,
      pages: {
        nextPageLink,
        prevPageLink,
        lastPageLink,
        totalPage,
      },
      message,
    };
  } catch (error) {
    return {
      success: false,
      message: getAxiosErrorMessage(error),
      canceled: error.code === "ERR_CANCELED",
    };
  }
};

export const postGlobalSpecs = async (payload, customerContext) => {
  try {
    const requestPayload = {
      test_display_name: payload.test_display_name,
      test_spec: payload.test_spec,
      update_operation: payload.update_operation,
      product_list: payload.product_list,
    };

    const response = await requestEsvThroughWrapper({
      apiName: "globalspecs/",
      callType: HTTP_METHOD.POST,
      customerContext,
      payload: requestPayload,
    });
    const { message } = response.data;
    return { success: message === "Success", message };
  } catch (error) {
    return {
      success: false,
      message: getAxiosErrorMessage(error),
    };
  }
};

export const getAllProducts = async (signal, customerContext) => {
  try {
    const response = await requestEsvThroughWrapper({
      apiName: "getallproducts/",
      callType: HTTP_METHOD.GET,
      customerContext,
      payload: {},
      signal,
    });
    const { message } = response.data;
    const products = response.data.result;
    return { success: message === "Success", products, message };
  } catch (error) {
    return {
      success: false,
      message: getAxiosErrorMessage(error),
    };
  }
};

export const getSpecsUnits = async (signal, customerContext) => {
  try {
    const response = await requestEsvThroughWrapper({
      apiName: "getspecsunits/",
      callType: HTTP_METHOD.GET,
      customerContext,
      payload: {},
      signal,
    });
    const { message } = response.data;
    return { success: message === "Success", result: response.data.result, message };
  } catch (error) {
    return {
      success: false,
      message: getAxiosErrorMessage(error),
    };
  }
};

export const getTestSuggestions = async (search, sampleCategory = "product", customerContext) => {
  try {
    const response = await requestEsvThroughWrapper({
      apiName: "testsuggestions/",
      callType: HTTP_METHOD.GET,
      customerContext,
      payload: {
        search,
        side: sampleCategory,
        display_name_only: "True",
      },
    });
    return {
      success: response.data.message === "Success",
      message: response.data.message,
      testList: response.data.result,
    };
  } catch (error) {
    return { success: false, message: "Something went wrong." };
  }
};

export const getProductFields = async (signal, customerContext) => {
  try {
    const response = await requestEsvThroughWrapper({
      apiName: "productfields/",
      callType: HTTP_METHOD.GET,
      customerContext,
      payload: {},
      signal,
    });
    const responseMessage = response.data && response.data.message;
    const result = response.data && response.data.result;

    if (responseMessage && responseMessage !== "Success" && responseMessage !== "success") {
      return {
        success: false,
        message: responseMessage,
        isWarning: responseMessage === SPEC_MANAGER_NOT_ONBOARDED_ON_PRODUCT_MESSAGE,
      };
    }

    const fields = result && result.fields;
    const linkReport = result && result.link_report;
    if (Array.isArray(fields) && fields.length > 0) {
      return {
        success: true,
        fields,
        link_report: linkReport,
      };
    }
    return {
      success: false,
      message: responseMessage || "No data from API.",
    };
  } catch (error) {
    if (error.code === "ERR_CANCELED") {
      return {
        success: false,
        message: "",
        canceled: true,
      };
    }
    const message = getAxiosErrorMessage(error);
    return {
      success: false,
      message,
      isWarning: message === SPEC_MANAGER_NOT_ONBOARDED_ON_PRODUCT_MESSAGE,
    };
  }
};

export const getEnvironmentFields = async (signal, customerContext) => {
  try {
    const response = await requestEsvThroughWrapper({
      apiName: "environmentfields/",
      callType: HTTP_METHOD.GET,
      customerContext,
      payload: {},
      signal,
    });
    const responseMessage = response.data && response.data.message;

    if (responseMessage !== "Success" && responseMessage !== "success") {
      const message = responseMessage || "Something went wrong.";
      return {
        success: false,
        message,
        isWarning: message === SPEC_MANAGER_NOT_ONBOARDED_ON_ENVIRONMENT_MESSAGE,
      };
    }

    const result = response.data.result;
    return {
      success: true,
      message: responseMessage,
      fields: result.fields,
      sampleid_fields: result.sampleid_fields,
      link_pattern_fields: result.link_pattern_fields,
      index0: result.index0,
      delimiter: result.delimeter,
      link_pattern: result.link_pattern,
    };
  } catch (error) {
    if (error.code === "ERR_CANCELED") {
      return {
        success: false,
        message: "",
        canceled: true,
      };
    }
    const message = getAxiosErrorMessage(error);
    return {
      success: false,
      message,
      isWarning: message === SPEC_MANAGER_NOT_ONBOARDED_ON_ENVIRONMENT_MESSAGE,
      canceled: false,
    };
  }
};

/**
 * Location spec read/write for a single location.
 * GET/PATCH envspecmanager/ — requires location_id (spec rows for that location).
 */
export const getEnvSpecsData = async (payload, isUpdating = false, signal, customerContext) => {
  try {
    const locationId = payload.location_id || "";
    if (!locationId) {
      return {
        success: false,
        message: "location_id is required for envspecmanager.",
      };
    }

    const requestPayload = {
      location_id: locationId,
      specs: payload.specs,
      page: payload.page,
    };

    let response;
    if (isUpdating) {
      requestPayload.action = payload.action || "update_specs";
      response = await requestEsvThroughWrapper({
        apiName: "envspecmanager/",
        callType: HTTP_METHOD.PATCH,
        customerContext,
        payload: requestPayload,
        signal,
      });
    } else {
      response = await requestEsvThroughWrapper({
        apiName: "envspecmanager/",
        callType: HTTP_METHOD.GET,
        customerContext,
        payload: requestPayload,
        signal,
      });
    }

    const emptyPages = {
      prevPageLink: "",
      nextPageLink: "",
      lastPageLink: "",
      totalPage: "",
    };

    const message = response.data && response.data.message;
    if (!isUpdating && (message === "Success" || message === "success")) {
      return {
        success: true,
        files: response.data.result,
        pattern: "",
        pages: emptyPages,
      };
    }
    if (message === "Specs and status updated" || message === "Specs and status updated, reports from last 30 days updated") {
      return { success: true };
    }

    return { success: false, message: (response.data && response.data.message) || "Something went wrong." };
  } catch (error) {
    return {
      success: false,
      message: "Something went wrong.",
      canceled: error.code === "ERR_CANCELED",
    };
  }
};

/**
 * Global environment points/locations (Environment tab parity).
 * GET getpoints/ — company-scoped list; not used for test spec (see envglobalspecs/).
 */
export const getEnvPoints = async (payload = {}, signal, customerContext) => {
  try {
    const requestPayload = {
      title: payload.mapTitle || "",
      search: payload.search || "",
      spec_flag: payload.specFlag != null ? payload.specFlag : -1,
      date_from: payload.dateFrom || "",
      date_to: payload.dateTo || "",
    };
    if (payload.filter) {
      requestPayload.filter = payload.filter;
    }

    const response = await requestEsvThroughWrapper({
      apiName: "getpoints/",
      callType: HTTP_METHOD.GET,
      customerContext,
      payload: requestPayload,
      signal,
    });

    if (!response.data || response.data.message !== "Success" || !Array.isArray(response.data.result)) {
      return {
        success: false,
        message: (response.data && response.data.message) || "No data.",
        locations: [],
      };
    }

    const locations = response.data.result.map((point) => {
      const locationIdentifier = resolvePointLocationIdentifier(point);
      return {
        ...point,
        location_id: point.location_id,
        location_identifier: locationIdentifier,
      };
    });

    return { success: true, locations };
  } catch (error) {
    return {
      success: false,
      message: getAxiosErrorMessage(error),
      locations: [],
      canceled: error.code === "ERR_CANCELED",
    };
  }
};

export const getEnvGlobalSpecsList = async (options = {}, signal, customerContext) => {
  try {
    const { filter = "", search = "", page = 1 } = options;
    const requestPayload = {
      page: Number(page) || 1,
    };
    if (filter) requestPayload.filter = filter;
    if (search && String(search).trim()) requestPayload.search = String(search).trim();

    const response = await requestEsvThroughWrapper({
      apiName: "envglobalspecs/",
      callType: HTTP_METHOD.GET,
      customerContext,
      payload: requestPayload,
      signal,
    });

    if (!response.data || response.data.message !== "Success" || !Array.isArray(response.data.result)) {
      return { success: true, result: [], nextPageLink: "" };
    }

    const result = response.data.result.map((item) => {
      const rawStatus = item.spec_status;
      const normalizedStatus = rawStatus === "specs_added" ? "added" : rawStatus;
      return {
        test: item.test,
        spec_status: normalizedStatus,
        products_count: item.locations_count,
        standard_specs: item.standard_specs,
      };
    });

    return {
      success: true,
      result,
      nextPageLink: (response.data && response.data.next_page_link) || "",
    };
  } catch (error) {
    return {
      success: false,
      message: getAxiosErrorMessage(error),
      canceled: error.code === "ERR_CANCELED",
      result: [],
      nextPageLink: "",
    };
  }
};

const normalizeEnvGlobalSpecsApiResult = (data) => {
  const rawLocations = Array.isArray(data.locations) ? data.locations : [];
  const locations = rawLocations.map((point) => {
    const locationIdentifier = resolvePointLocationIdentifier(point);
    return {
      ...point,
      location_id: point.location_id,
      location_identifier: locationIdentifier,
    };
  });

  return {
    test: data.test,
    min: data.min,
    max: data.max,
    unit: data.unit,
    countries_list: data.countries_list,
    locations,
  };
};

/**
 * envglobalspecs/ GET with test_display_name.
 * Returns shared test spec (min/max/unit) and locations that use the test.
 */
export const getEnvGlobalSpecsByTest = async (payload, customerContext) => {
  try {
    const response = await requestEsvThroughWrapper({
      apiName: "envglobalspecs/",
      callType: HTTP_METHOD.GET,
      customerContext,
      payload: {
        test_display_name: (payload && payload.test_display_name != null) ? payload.test_display_name : "",
      },
    });

    if (!response.data || response.data.message !== "Success" || !response.data.result) {
      return { success: false, message: "No data.", result: null };
    }

    return {
      success: true,
      result: normalizeEnvGlobalSpecsApiResult(response.data.result),
    };
  } catch (error) {
    return {
      success: false,
      message: getAxiosErrorMessage(error),
      result: null,
    };
  }
};

export const postEnvGlobalSpecs = async (payload, customerContext) => {
  try {
    const { test_display_name: testDisplayName, test_spec: testSpec, location_id_list: locationIdList } = payload || {};
    if (!testDisplayName || !testSpec || typeof testSpec !== "object") {
      return { success: false, message: "test_display_name and test_spec are required." };
    }

    const requestPayload = {
      test_display_name: testDisplayName,
      test_spec: {
        min: testSpec.min != null ? testSpec.min : "",
        max: testSpec.max != null ? testSpec.max : "",
        unit: testSpec.unit != null ? testSpec.unit : "",
      },
    };

    if (
      Array.isArray(testSpec.countries_lst)
      && testSpec.countries_lst.length > 0
    ) {
      requestPayload.test_spec.countries_lst = testSpec.countries_lst;
    }

    if (Array.isArray(locationIdList) && locationIdList.length > 0) {
      requestPayload.location_id_list = locationIdList;
    }

    const response = await requestEsvThroughWrapper({
      apiName: "envglobalspecs/",
      callType: HTTP_METHOD.POST,
      customerContext,
      payload: requestPayload,
    });
    if (response.data && response.data.message === "Success") {
      return { success: true, message: response.data.message };
    }
    return { success: false, message: (response.data && response.data.message) || "Update failed." };
  } catch (error) {
    return {
      success: false,
      message: getAxiosErrorMessage(error),
    };
  }
};
